import React, {useEffect, useRef, useState} from "react";
import {ProgressSpinner} from "primereact/progressspinner";
import {Message} from "primereact/message";
import FormRenderer, {EndingView} from "./form-renderer";
import {formsApi, formsErrorDetails, formsErrorKey} from "./forms-api";
import {Icon, useT} from "./host";

// app.zofino.ir/#/f/<token>: the respondent's page, outside sign-in (rendered before <Auth/> in App.js).
const newRequestId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function utmOf() {
    const query = new URLSearchParams(window.location.search || (window.location.hash.split("?")[1] || ""));
    const out = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((key) => {
        if (query.get(key)) out[key] = query.get(key);
    });
    return out;
}

export default function PublicFormPage({token}) {
    const t = useT();
    const [state, setState] = useState({status: "loading"});
    const [submitting, setSubmitting] = useState(false);
    const [serverErrors, setServerErrors] = useState({});
    const [formError, setFormError] = useState("");
    const [ending, setEnding] = useState(null);
    const [round, setRound] = useState(0);
    // one id per attempt: a network retry of the same click is stored once (server idempotency)
    const requestId = useRef(newRequestId());

    useEffect(() => {
        let alive = true;
        formsApi.publicForm(token)
            .then((result) => alive && setState(result?.available ? {status: "ready", form: result.form} : {status: "closed", reason: result?.reason, title: result?.title}))
            .catch(() => alive && setState({status: "closed", reason: "invalid"}));
        return () => { alive = false; };
    }, [token]);

    useEffect(() => {
        const title = state.form?.meta?.title || state.title;
        if (title) document.title = title;
    }, [state]);

    const submit = async (answers) => {
        setSubmitting(true);
        setServerErrors({});
        setFormError("");
        try {
            const result = await formsApi.publicSubmit(token, {answers, requestId: requestId.current, utm: utmOf()});
            setEnding({...(result.ending || {}), tracking: result.tracking});
            if (result.ending?.type === "redirect" && result.ending.url && !result.ending.newTab) setTimeout(() => window.location.assign(result.ending.url), 1500);
        } catch (error) {
            const details = formsErrorDetails(error);
            if (details.fieldErrors && Object.keys(details.fieldErrors).length) setServerErrors(details.fieldErrors);
            if (details.reason) setFormError(t(`forms.public.closed.${details.reason}`));
            else setFormError(t(formsErrorKey(error, "forms.error.submit-failed")));
        } finally {
            setSubmitting(false);
        }
    };

    const upload = async (field, file) => {
        try {
            return await formsApi.publicUpload(token, field, file);
        } catch (error) {
            throw new Error(t(formsErrorKey(error, "forms.error.upload")));
        }
    };

    const restart = () => {
        requestId.current = newRequestId();
        setEnding(null);
        setRound((value) => value + 1);
    };

    return (
        <div dir="rtl" className="rtl h-full w-full overflow-auto surface-ground flex flex-column align-items-center gap-3 py-5 px-3">
            <main className="w-full surface-card border-1 border-200 border-round-2xl border-top-3 border-primary p-4 md:p-5 md:w-8 lg:w-6 xl:w-5">
                {state.status === "loading" && <div className="flex justify-content-center p-6"><ProgressSpinner strokeWidth="4" className="w-3rem h-3rem"/></div>}
                {state.status === "closed" && (
                    <div className="flex flex-column align-items-center text-center gap-3 py-5">
                        <span className="surface-100 border-circle p-4 flex align-items-center justify-content-center"><Icon icon="mi mi:lock" className="text-3xl text-500"/></span>
                        {state.title && <h2 className="m-0 text-lg">{state.title}</h2>}
                        <span className="text-sm text-600 line-height-3">{t(`forms.public.closed.${state.reason || "invalid"}`)}</span>
                    </div>
                )}
                {state.status === "ready" && (ending
                    ? <EndingView ending={ending} onRestart={restart}/>
                    : (
                        <div className="flex flex-column gap-3">
                            <FormRenderer key={round} definition={state.form} onSubmit={submit} upload={upload} submitting={submitting} serverErrors={serverErrors}/>
                            {formError && <Message severity="error" className="w-full justify-content-start text-xs" text={formError}/>}
                        </div>
                    ))}
            </main>
            <span className="text-xs text-500">{t("forms.public.made-with")}</span>
        </div>
    );
}
